import request from '@hb/sharp-request';
import { gatewayUrl, isUseGeneralAgreesApi } from '@/constant';
import requestDefault from '@/shared/utils/request';
import { ubtSendError } from '@/shared/utils/ubt';
import { AgreementPo } from '@/po/agreement';
import type {
  AgreementSceneType, RegisterUserAgreement, UserContractType, GeneralContracts,
} from './types';

import { RechargeAppContractTypeEnums, GeneralMarkOneAgreement } from './types';

export const formatAgreementsMarkParams = (agreements: AgreementPo[])
: GeneralMarkOneAgreement[] => {
  const contracts = {} as Record<number, GeneralMarkOneAgreement>;

  if (agreements && agreements.length) {
    agreements.forEach(({ agreementSource = -1, id }) => {
      let oneSource = contracts[agreementSource];

      if (!oneSource) {
        oneSource = { agreementSource, agreementIds: [] };
        contracts[agreementSource] = oneSource;
      }

      oneSource.agreementIds.push(id);
    });
  }

  return Object.values(contracts);
};

/** 多平台通用(哈臻快、微信小程序、哈啰app)查询待签署协议 */
export const generalQueryRegisterAgrees = (assignPlatform?: number)
: Promise<GeneralContracts> => requestDefault.postGate(
  'f.general.queryRegisterAgrees',
  { assignPlatform },
  {
    showLoading: undefined,
  },
);

/** 多平台通用(哈臻快、微信小程序、哈啰app)查询已签署协议(已留痕) */
export const generalQueryUserAgreeRecords = (assignPlatform?: number)
  : Promise<GeneralContracts> => requestDefault.postGate(
  'f.general.queryUserAgreeRecords',
  { assignPlatform },
  {
    showLoading: undefined,
  },
);

/** 多平台通用(哈臻快、微信小程序、哈啰app) 签署协议 */
export const generalMarkAgreements = (agreements: AgreementPo[], assignPlatform?: number)
  : Promise<boolean> => requestDefault.postGate(
  'f.general.markAgreements',
  { agreementList: formatAgreementsMarkParams(agreements), assignPlatform },
  {
    showLoading: undefined,
  },
);

export const getAgreedInfo = (sceneType?: AgreementSceneType, assignPlatform?: number)
: Promise<UserContractType> => {
  if (isUseGeneralAgreesApi()) {
    return generalQueryRegisterAgrees(assignPlatform).then(({ agreements }) => ({
      agreements: agreements.map((it) => ({ ...it, isAgreed: false })),
      isAgreed: !agreements.length,
    }));
  }

  return requestDefault.postGate(
    'f.loan.agreement.agreement',
    { sceneType, assignPlatform },
    { showLoading: undefined },
  );
};

export const getSkAppLoginContracts = ()
: Promise<UserContractType> => requestDefault.postGate(
  'f.none.login.agreement',
  { sceneType: 3 },
  {
    showLoading: undefined,
  },
);

/** 哈臻快a面协议(服务端只限 identification = 1：a面场景，b面逻辑已废弃) */
export const getRechargeTypeAContracts = ()
: Promise<UserContractType> => requestDefault.postGate(
  'f.newapp.ab.contractlist',
  { identification: RechargeAppContractTypeEnums.TYPE_A },
  {
    showLoading: undefined,
  },
);

/** 哈臻快a面协议留痕 */
export const markRechargeAppContracts = (agreements: AgreementPo[])
  : Promise<void | unknown> => requestDefault.postGate(
  'f.newapp.agreement.remains', { agreementList: formatAgreementsMarkParams(agreements) },
);

/**
 * 查询用户已同意的平台协议
 * yapi: https://yapi.hellobike.cn/project/2033/interface/api/328889
 * 张浩东
 */
export const getRegisteredAgreements = (): Promise<RegisterUserAgreement[]> => requestDefault
  .postGate(
    'f.user.agree.queryUserAgreeRecords',
  );

/**
 * 查询用户待签署的平台协议(登录态)
 * yapi: 张浩东,https://yapi.hellobike.cn/project/2033/interface/api/328887
 */
export const getWaitRegisterAgrees = ()
: Promise<RegisterUserAgreement[]> => requestDefault.postGate(
  'f.user.agree.queryRegisterAgrees', {},
);

/**
 * 查询用户待签署的平台协议(未登录态)
 * yapi: 张浩东,https://yapi.hellobike.cn/project/2033/interface/api/328887
 */
export const getNoLoginRegisterAgrees = (): Promise<RegisterUserAgreement[]> => request.postGate(
  'f.user.agree.queryRegisterAgrees', {}, {
    baseURL: gatewayUrl,
    showErrMsg: undefined,
  },
);

/**
 * 协议签署接口
 * 功能描述：同意协议
 * yapi: 张浩东,https://yapi.hellobike.cn/project/2033/interface/api/331889
 */
export const markUserAgreements = (agreementIds: string[])
  : Promise<void | unknown> => requestDefault.postGate(
  'f.user.agree.markAgreements', { agreementIds },
).catch((error) => ubtSendError(error));


const AgreementApi = {
  getAgreedInfo,
  getSkAppLoginContracts,
  getWaitRegisterAgrees,
  getRegisteredAgreements,
  getNoLoginRegisterAgrees,
  markUserAgreements,
  getRechargeTypeAContracts,
  markRechargeAppContracts,
  generalQueryRegisterAgrees,
  generalQueryUserAgreeRecords,
  generalMarkAgreements,
};

export default AgreementApi;
